import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { TextField, TextFieldTheme } from "@wix/wix-ui-tpa/cssVars";
import classNames from "classnames";
import { useRef, useMemo, useState, useCallback } from "react";
import { useConfig } from "../../contexts/config-context.js";
import { useFocusFieldEvent } from "../../contexts/events-context.js";
import { useForcedState, ForcedState } from "../../contexts/forced-state-context.js";
import { useTranslation } from "../../contexts/translation-context.js";
import { useFieldAttributes } from "../../services/field-attributes.js";
import { FieldDescriptionWUT } from "../field-description/field-description-wut.js";
import { FieldErrorWUT } from "../field-error/field-error-wut.js";
import { FieldLabelWUT } from "../field-label/field-label-wut.js";
import { formatPrice, getCurrencyAriaLabel } from "../shared/payment-field-utils.js";
import { useInvokeCallback } from "../shared/use-invoke-callback.js";
import { Widget } from "../widget/widget.js";
import { toProductValue } from "./payment-input-field-utils.js";
import { classes } from "./payment-input-field-wut.st.css";
const PaymentInputFieldWUT = ({
  id,
  target,
  label,
  hideLabel = false,
  placeholder,
  required = false,
  value = null,
  onChange,
  onFocus,
  onBlur,
  error,
  hasError = false,
  errorMessage,
  disabled = false,
  readOnly,
  description,
  validation,
  FieldLayout,
  requiredIndicatorType,
  requiredIndicatorPlacement,
  widgetHtml,
  widgetEnabled
}) => {
  const { t } = useTranslation();
  const {
    labelId,
    inputId,
    errorId,
    descriptionId,
    ariaDescribedBy,
    prefixId
  } = useFieldAttributes(id, error, description);
  const { currency, regionalFormat } = useConfig();
  const forcedHover = useForcedState().includes(ForcedState.INPUT_FIELDS_HOVER);
  const invokeBlur = useInvokeCallback(onBlur);
  const inputRef = useRef(null);
  const paymentValidation = useMemo(
    () => validation?.predefined?.paymentOptions?.products?.[0],
    [validation]
  );
  const [paymentValue, setPaymentValue] = useState(
    String(value?.at(0)?.price ?? "")
  );
  const handleOnBlur = useCallback(
    (event) => {
      const price = formatPrice(event.target.value, regionalFormat);
      setPaymentValue(price ? price : "");
      invokeBlur();
    },
    [regionalFormat, invokeBlur]
  );
  const handleOnChange = useCallback(
    (event) => {
      setPaymentValue(event.target.value);
      const filteredInput = formatPrice(event.target.value, regionalFormat);
      if (filteredInput) {
        const normalizedValue = Number(filteredInput.replace(/,/g, "."));
        onChange([toProductValue(paymentValidation, normalizedValue)]);
      } else {
        onChange(null);
      }
    },
    [setPaymentValue, onChange, paymentValidation, regionalFormat]
  );
  useFocusFieldEvent(() => {
    inputRef.current?.focus({ preventScroll: true });
  }, id);
  return /* @__PURE__ */ jsx(
    FieldLayout,
    {
      fieldId: id,
      renderLabel: () => /* @__PURE__ */ jsx(
        FieldLabelWUT,
        {
          labelId,
          required,
          label,
          hideLabel,
          requiredIndicatorType,
          requiredIndicatorPlacement
        }
      ),
      renderInput: () => widgetHtml && widgetEnabled ? /* @__PURE__ */ jsx(
        Widget,
        {
          fieldId: id,
          widgetHtml,
          onChange,
          value
        }
      ) : /* @__PURE__ */ jsx(
        TextField,
        {
          id: inputId,
          placeholder,
          "aria-label": label,
          "aria-labelledby": `${labelId} ${prefixId}`,
          "aria-describedby": ariaDescribedBy,
          className: classNames(classes.paymentInput, classes.theme, {
            [classes.hover]: forcedHover
          }),
          value: value ? paymentValue : "",
          name: target,
          onChange: handleOnChange,
          disabled,
          readOnly,
          onBlur: handleOnBlur,
          onFocus,
          theme: TextFieldTheme.Box,
          error: hasError,
          newErrorMessage: true,
          prefix: currency ? /* @__PURE__ */ jsx(
            "span",
            {
              id: prefixId,
              "aria-hidden": false,
              "aria-label": getCurrencyAriaLabel(t, currency),
              children: currency.sign || currency.code
            }
          ) : null,
          autoComplete: "off",
          required,
          ref: inputRef
        }
      ),
      renderDescription: () => /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx(
          FieldErrorWUT,
          {
            target,
            hasError,
            errorMessage,
            id: errorId
          }
        ),
        /* @__PURE__ */ jsx(FieldDescriptionWUT, { description, id: descriptionId })
      ] })
    }
  );
};
export {
  PaymentInputFieldWUT
};
//# sourceMappingURL=payment-input-field-wut.js.map
